
import React, { useState } from "react";
import { useSelector } from 'react-redux';
import axios from "axios";
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  TextField,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Autocomplete,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert,
} from "@mui/material";
import Layout from "../components/layout/GSTlayout";
import config from "../config.js";

const TaskAssignmentPage = () => {
  const userId = useSelector((state) => state.auth.user?.id);
  
  const { API_BASE_URL } = config;
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });


  const [employees,] = useState([
    {
      id: 1,
      name: "John Doe",
      department: "GST Compliance",
      email: "john.doe@company.com",
    },
    {
      id: 2,
      name: "Jane Smith",
      department: "Tax Reporting",
      email: "jane.smith@company.com",
    },
    {
      id: 3,
      name: "Mike Johnson",
      department: "Audit",
      email: "mike.johnson@company.com",
    },
  ]);

  const [newTask, setNewTask] = useState({
    clientName: "",
    taskType: "",
    description: "",
    assignedTo: null,
    priority: "Medium",
    dueDate: "",
    status: "Pending",
  });

  const [openDialog, setOpenDialog] = useState(false);

  const taskTypes = [
    "GST Filing",
    "Audit",
    "Tax Planning",
    "Compliance",
    "Consultation",
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewTask((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleAssigneeChange = (event, newValue) => {
    setNewTask((prev) => ({
      ...prev,
      assignedTo: newValue?.id || null,
    }));
  };

  const showSnackbar = (message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleSubmitTask = async () => {
    if (!userId) {
      showSnackbar("User not authenticated", "error");
      return;
    }

    try {
      const response = await axios.post(`${API_BASE_URL}/api/v1/createTasks`, {
        ...newTask,
        userId: userId,
      });

      if (response.data && response.data.status === "success") {
        showSnackbar("Task created successfully");
      
        setNewTask({
          clientName: "",
          taskType: "",
          description: "",
          assignedTo: null,
          priority: "Medium",
          dueDate: "",
          status: "Pending",
        });
        setOpenDialog(false);
      } else {
        showSnackbar("Error creating task", "error");
      }
    } catch (error) {
      showSnackbar(
        error.response?.data?.message || "Error creating task",
        "error"
      );
      console.error("Error:", error);
    }
  };

  const validateTask = () => {
    return (
      newTask.clientName &&
      newTask.taskType &&
      newTask.dueDate &&
      newTask.priority
    );
  };

  return (
    <Layout>
      <Box sx={{ flexGrow: 1, p: 3 }}>
        <Typography variant="h4" gutterBottom>
          Task Assignment
        </Typography>

        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  Create New Task
                </Typography>

                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Client Name"
                      name="clientName"
                      value={newTask.clientName}
                      onChange={handleInputChange}
                      variant="outlined"
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth variant="outlined">
                      <InputLabel>Task Type</InputLabel>
                      <Select
                        name="taskType"
                        value={newTask.taskType}
                        onChange={handleInputChange}
                        label="Task Type"
                      >
                        {taskTypes.map((type) => (
                          <MenuItem key={type} value={type}>
                            {type}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Autocomplete
                      options={employees}
                      getOptionLabel={(option) => option.name}
                      value={employees.find(emp => emp.id === newTask.assignedTo) || null}
                      onChange={handleAssigneeChange}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Assign To"
                          variant="outlined"
                        />
                      )}
                      renderOption={(props, option) => (
                        <li {...props} key={option.id}>
                          <Box sx={{ display: "flex", flexDirection: "column" }}>
                            <Typography variant="body1">
                              {option.name}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {option.department}
                            </Typography>
                          </Box>
                        </li>
                      )}
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth variant="outlined">
                      <InputLabel>Priority</InputLabel>
                      <Select
                        name="priority"
                        value={newTask.priority}
                        onChange={handleInputChange}
                        label="Priority"
                      >
                        <MenuItem value="Low">
                          <Chip label="Low" color="success" size="small" />
                        </MenuItem>
                        <MenuItem value="Medium">
                          <Chip label="Medium" color="warning" size="small" />
                        </MenuItem>
                        <MenuItem value="High">
                          <Chip label="High" color="error" size="small" />
                        </MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Due Date"
                      type="date"
                      name="dueDate"
                      value={newTask.dueDate}
                      onChange={handleInputChange}
                      InputLabelProps={{
                        shrink: true,
                      }}
                      variant="outlined"
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Task Description"
                      name="description"
                      value={newTask.description}
                      onChange={handleInputChange}
                      multiline
                      rows={4}
                      variant="outlined"
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={() => setOpenDialog(true)}
                      disabled={!validateTask()}
                    >
                      Assign Task
                    </Button>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

      
        <Dialog open={openDialog} onClose={() => setOpenDialog(false)}>
          <DialogTitle>Confirm Task Assignment</DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to assign this task to{" "}
              {employees.find(emp => emp.id === newTask.assignedTo)?.name}?
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenDialog(false)} color="secondary">
              Cancel
            </Button>
            <Button onClick={handleSubmitTask} color="primary" autoFocus>
              Confirm
            </Button>
          </DialogActions>
        </Dialog>

        
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
        >
          <Alert
            onClose={handleCloseSnackbar}
            severity={snackbar.severity}
            sx={{ width: "100%" }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </Layout>
  );
};

export default TaskAssignmentPage;